import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
} from '@enamelai/design-system'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { StaffScope } from '@constants/Settings/staffPermission.const'
import {
  getRolePermissions,
  saveRolePermissions,
} from '@services/api/role-permissions.api'
import { hasStaffScope } from '@utils/staffScope.util'
import {
  permissionRoleColumns,
  rolePermissionsApiToRows,
  rowsToSaveRolesPayload,
  type PermissionsMatrixRow,
  type PermissionsRoleColumnKey,
} from '@utils/staffPermission.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import './PermissionsStyle.css'

const LOADING_CONTAINER = '#scp-staff-permissions'

const ROLE_COLUMN_LABELS: Record<PermissionsRoleColumnKey, string> = {
  manager: 'Manager',
  provider: 'Provider',
  hygienist: 'Hygienist',
  dentalAsst: 'Dental Asst.',
  frontDesk: 'Front desk',
  accounting: 'Billing',
}

const cloneRows = (source: PermissionsMatrixRow[]): PermissionsMatrixRow[] =>
  source.map((r) => ({ ...r }))

const rowsMatchBaseline = (
  current: PermissionsMatrixRow[],
  baseline: PermissionsMatrixRow[],
): boolean => {
  if (current.length !== baseline.length) return false
  return current.every((row, index) => {
    const saved = baseline[index]
    if (!saved || row.id !== saved.id) return false
    return permissionRoleColumns.every((role) => row[role] === saved[role])
  })
}

const Permissions = (): React.ReactElement => {
  const { setHeaderButtons } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const canEdit = hasStaffScope(StaffScope.SETTINGS_WRITE)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [rows, setRows] = useState<PermissionsMatrixRow[]>([])
  const savedBaselineRef = useRef<PermissionsMatrixRow[]>([])
  const [sort, toggleSort] = useTableSort()

  const applyRows = useCallback((next: PermissionsMatrixRow[]): void => {
    savedBaselineRef.current = cloneRows(next)
    setRows(next)
  }, [])

  const loadPermissions = useCallback(async (): Promise<void> => {
    setLoading(true)
    try {
      const res = await getRolePermissions()
      if (res.statusCode === 200 && Array.isArray(res.data?.role_permissions)) {
        applyRows(rolePermissionsApiToRows(res.data.role_permissions))
      } else {
        showToast(
          createFailedToast(
            'Permissions',
            res.message || 'Unable to load permission.',
          ),
        )
      }
    } catch (e: unknown) {
      console.error(e)
      showToast(createFailedToast('Permissions', 'Unable to load permission.'))
    } finally {
      setLoading(false)
    }
  }, [applyRows, showToast])

  useSettingsSubpageLoading(loading)

  useEffect(() => {
    void loadPermissions()
  }, [loadPermissions])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!canEdit || saving || rows.length === 0) return
    setSaving(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const payload = rowsToSaveRolesPayload(rows)
      const saveRes = await saveRolePermissions(payload)
      if (saveRes.statusCode !== 200) {
        showToast(
          createFailedToast(
            'Permissions',
            saveRes.message || 'Unable to save changes.',
          ),
        )
        return
      }
      const refresh = await getRolePermissions()
      if (
        refresh.statusCode !== 200 ||
        !Array.isArray(refresh.data?.role_permissions)
      ) {
        showToast(
          createFailedToast(
            'Permissions',
            refresh.message || 'Saved but refresh failed.',
          ),
        )
        return
      }
      applyRows(rolePermissionsApiToRows(refresh.data.role_permissions))
      showToast(createSuccessToast('Saved', 'Permission updated.'))
    } catch (e: unknown) {
      console.error(e)
      showToast(createFailedToast('Permissions', 'Unable to save changes.'))
    } finally {
      setSaving(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [applyRows, canEdit, hideLoading, rows, saving, showLoading, showToast])

  const handleCancel = useCallback((): void => {
    setRows(cloneRows(savedBaselineRef.current))
  }, [])

  const hasChanges = useMemo(
    () => !rowsMatchBaseline(rows, savedBaselineRef.current),
    [rows],
  )

  const actionsDisabled = loading || saving || rows.length === 0

  useEffect(() => {
    if (!canEdit) {
      setHeaderButtons(null)
      return () => setHeaderButtons(null)
    }

    setHeaderButtons(
      <>
        <Button
          className="settings-ds-button"
          variant="secondary"
          size="md"
          type="button"
          disabled={!hasChanges || actionsDisabled}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          loading={saving}
          disabled={!hasChanges || actionsDisabled}
          onClick={() => {
            void handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )
    return () => setHeaderButtons(null)
  }, [
    canEdit,
    setHeaderButtons,
    saving,
    handleSave,
    handleCancel,
    hasChanges,
    actionsDisabled,
  ])

  const setPermission = useCallback(
    (rowId: string, role: PermissionsRoleColumnKey, next: boolean): void => {
      setRows((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, [role]: next } : r)),
      )
    },
    [],
  )

  const columns = useMemo<DataTableColumn<PermissionsMatrixRow>[]>(
    () => [
      { id: 'module', label: 'Module', sortable: true, width: '20%' },
      {
        id: 'admin',
        label: 'Admin',
        align: 'center',
        renderCell: (row) => (
          <Checkbox
            className="perm-checkbox"
            aria-label={`Admin: ${row.module}`}
            checked
            disabled
            readOnly
          />
        ),
      },
      ...permissionRoleColumns.map(
        (role): DataTableColumn<PermissionsMatrixRow> => ({
          id: role,
          label: ROLE_COLUMN_LABELS[role],
          align: 'center',
          renderCell: (row) => (
            <Checkbox
              className="perm-checkbox"
              aria-label={`${ROLE_COLUMN_LABELS[role]}: ${row.module}`}
              checked={row[role]}
              disabled={!canEdit || saving}
              onChange={(e) => setPermission(row.id, role, e.target.checked)}
            />
          ),
        }),
      ),
    ],
    [canEdit, saving, setPermission],
  )

  const sortedRows = useMemo(() => sortRows(rows, sort), [rows, sort])
  const { pageRows, pagination } = usePagedRows(sortedRows)

  const tableState = loading
    ? 'loading'
    : rows.length === 0
      ? 'empty'
      : 'default'

  return (
    <div className="permissions-settings" id="scp-staff-permissions">
      <DataTable<PermissionsMatrixRow>
        className="perm-table"
        columns={columns}
        rows={pageRows}
        state={tableState}
        emptyState={{
          title: 'No permissions found',
          description: "You don't have any permission settings on record yet.",
          actionLabel: 'Retry',
          onAction: () => {
            void loadPermissions()
          },
        }}
        sortColumnId={sort.columnId}
        sortDirection={sort.columnId ? sort.direction : undefined}
        onSort={toggleSort}
        showActions={false}
        pagination={pagination}
      />
    </div>
  )
}

export default Permissions
